import React from 'react';
import { Box, Typography, Divider, Button, Stack } from '@mui/material';
import { APP_VERSION, CHANGELOG } from '../../../constants/changelog';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
export default function About() {
  // Detect platform
  const getPlatformInfo = () => {
    if (typeof window !== 'undefined' && navigator.platform) {
      if (navigator.platform.toUpperCase().indexOf('WIN') > -1) {
        return { platform: 'windows', display: 'Windows' };
      }
      if (navigator.platform.toUpperCase().indexOf('MAC') > -1) {
        return { platform: 'mac', display: 'macOS' };
      }
    }
    return { platform: 'unknown', display: 'Desktop' };
  };

  const platformInfo = getPlatformInfo();
  const DOWNLOAD_VERSION = '0.7.1';  // Version of builds available for download
  const baseUrl = `https://files.pelmeshka.ch/downloads/${DOWNLOAD_VERSION}`;

  const getDownloadOptions = () => {
    switch (platformInfo.platform) {
      case 'windows':
        return [
          { 
            label: 'NSIS Setup',
            url: `${baseUrl}/Pelmeshka_Setup_${DOWNLOAD_VERSION}.exe`,
            filename: 'Pelmeshka Setup.exe' 
          },
          { 
            label: 'MSIX Package', 
            url: `${baseUrl}/Pelmeshka_${DOWNLOAD_VERSION}.msix`,
            filename: 'Pelmeshka.msix' 
          },
        ];
      case 'mac':
        return [
          { 
            label: 'DMG Bundle (Apple Silicon)',
            url: `${baseUrl}/Pelmeshka-${DOWNLOAD_VERSION}-arm64.dmg`,
            filename: 'Pelmeshka-arm64.dmg' 
          },
          { 
            label: 'DMG Bundle (Intel)',
            url: `${baseUrl}/Pelmeshka-${DOWNLOAD_VERSION}.dmg`,
            filename: 'Pelmeshka.dmg' 
          },
          { 
            label: 'ZIP Archive', 
            url: `${baseUrl}/Pelmeshka-${DOWNLOAD_VERSION}.zip`,
            filename: 'Pelmeshka.zip' 
          },
        ];
      default:
        return [
          { 
            label: 'View All Releases', 
            url: `https://github.com/ChebManu/pelmeshka/releases/tag/v${APP_VERSION}`,
            filename: '' 
          },
        ];
    }
  };

  const downloadOptions = getDownloadOptions();

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      pt={4}
      px={4}
      minWidth={300}
    >
      <Typography
        variant="h6"
        sx={{ color: '#2B1810', fontFamily: 'Courier New', mb: 1, letterSpacing: 2 }}
      >
        About Pelmeshka
      </Typography>
      <Typography
        sx={{ color: '#7A5035', fontFamily: 'Courier New', fontSize: '0.9rem', mb: 4 }}
      >
        Version {APP_VERSION}
      </Typography>

      {/* Download Section */}
      <Box
        sx={{
          minWidth: 320,
          maxWidth: 600,
          border: '1px solid #D4B896',
          borderRadius: '8px',
          p: 3,
          mb: 3,
          bgcolor: '#FBF5EC',
        }}
      >
        <Typography
          sx={{ color: '#7A5035', fontFamily: 'Courier New', fontSize: '0.75rem', letterSpacing: 2, mb: 2 }}
        >
          DOWNLOADS
        </Typography>
        <Typography
          sx={{ color: '#2B1810', fontFamily: 'Courier New', fontSize: '0.9rem', mb: 2 }}
        >
          {platformInfo.platform === 'unknown'
            ? 'Get Pelmeshka for your platform:'
            : `Downloads for ${platformInfo.display}:`}
        </Typography>
        <Stack spacing={1}>
          {downloadOptions.map((option, idx) => (
            <Button
              key={idx}
              variant="contained"
              startIcon={<FileDownloadIcon />}
              href={option.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: '#8B6F47',
                color: '#FBF5EC',
                fontFamily: 'Courier New',
                fontSize: '0.85rem',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: '#6B5437',
                },
              }}
            >
              {option.label}
              {option.filename && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.75rem',
                    fontFamily: 'Courier New',
                    display: 'block',
                    marginTop: '2px',
                  }}
                >
                  {option.filename}
                </Typography>
              )}
            </Button>
          ))}
        </Stack>
        {platformInfo.platform === 'windows' && (
          <Typography
            sx={{
              color: '#7A5035',
              fontFamily: 'Courier New',
              fontSize: '0.75rem',
              mt: 2,
              pt: 2,
              borderTop: '1px solid #D4B896',
            }}
          >
            Note: MSIX installer is built locally and available upon request.
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          minWidth: 320,
          maxWidth: 600,
          border: '1px solid #D4B896',
          borderRadius: '8px',
          p: 3,
          bgcolor: '#FBF5EC',
        }}
      >
        <Typography
          sx={{ color: '#7A5035', fontFamily: 'Courier New', fontSize: '0.75rem', letterSpacing: 2, mb: 2 }}
        >
          CHANGE LOG
        </Typography>

        {CHANGELOG.map((entry, idx) => (
          <Box key={entry.version} mb={idx === CHANGELOG.length - 1 ? 0 : 3}>
            <Typography
              sx={{ color: '#2B1810', fontFamily: 'Courier New', fontWeight: 'bold', fontSize: '0.95rem' }}
            >
              v{entry.version} — {entry.date}
            </Typography>
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
              {entry.changes.map((change, i) => (
                <Typography
                  key={i}
                  component="li"
                  sx={{ color: '#2B1810', fontFamily: 'Courier New', fontSize: '0.85rem', mb: 0.5 }}
                >
                  {change}
                </Typography>
              ))}
            </Box>
            {idx !== CHANGELOG.length - 1 && <Divider sx={{ mt: 2, borderColor: '#D4B896' }} />}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
